import { useState, useEffect } from "react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { AdminNavigation } from "@/components/AdminNavigation";
import { initializeChangelogWithEmailUpdate } from "@/lib/initializeChangelog";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Download, 
  FileText, 
  Search, 
  Filter, 
  Calendar,
  Clock,
  Trash2,
  AlertCircle,
  CheckCircle2,
  History
} from "lucide-react";
import { 
  loadChangelog, 
  exportChangelog, 
  clearChangelog, 
  getCategoryLabel,
  filterByCategory,
  filterByDateRange,
  type ChangeLogEntry 
} from "@/lib/changeLogger";
import { useToast } from "@/hooks/use-toast";
import { format, startOfDay, endOfDay, subDays, subMonths } from "date-fns";
import { de } from "date-fns/locale";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const AdminChangelog = () => {
  const { toast } = useToast();
  const [entries, setEntries] = useState<ChangeLogEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<ChangeLogEntry[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [dateFilter, setDateFilter] = useState<string>("all");

  useEffect(() => {
    // Initialize with email change entry
    initializeChangelogWithEmailUpdate();
    loadEntries();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [searchTerm, selectedCategory, dateFilter, entries]);

  const loadEntries = () => {
    const changelog = loadChangelog();
    setEntries(changelog);
  };

  const applyFilters = () => {
    let filtered = [...entries];

    // Kategorie-Filter
    if (selectedCategory !== "all") {
      filtered = filterByCategory(selectedCategory as ChangeLogEntry['category']);
    }

    // Datums-Filter
    if (dateFilter !== "all") {
      const now = new Date();
      let startDate = startOfDay(now);
      
      switch (dateFilter) {
        case "today":
          break;
        case "week":
          startDate = subDays(now, 7);
          break;
        case "month":
          startDate = subMonths(now, 1);
          break;
        case "quarter":
          startDate = subMonths(now, 3);
          break;
        case "year":
          startDate = subMonths(now, 12);
          break;
      }
      
      filtered = filterByDateRange(startDate, endOfDay(now));
    }

    // Such-Filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(entry => 
        entry.action.toLowerCase().includes(term) ||
        entry.description.toLowerCase().includes(term) ||
        getCategoryLabel(entry.category).toLowerCase().includes(term)
      );
    }

    setFilteredEntries(filtered);
  };

  const handleExport = (format: 'txt' | 'md' | 'csv') => {
    exportChangelog(format);
    toast({
      title: "Export erfolgreich",
      description: `Changelog wurde als ${format.toUpperCase()} heruntergeladen.`,
    });
  };

  const handleClear = () => {
    clearChangelog();
    setEntries([]);
    setFilteredEntries([]);
    toast({
      title: "Changelog gelöscht",
      description: "Alle Einträge wurden entfernt.",
      variant: "destructive"
    });
  };

  const getCategoryColor = (category: ChangeLogEntry['category']): string => {
    const colors: Record<ChangeLogEntry['category'], string> = {
      datenschutz: 'bg-blue-500/10 text-blue-700 border-blue-300',
      dokumente: 'bg-purple-500/10 text-purple-700 border-purple-300',
      betroffenenrechte: 'bg-orange-500/10 text-orange-700 border-orange-300',
      backup: 'bg-green-500/10 text-green-700 border-green-300',
      iso27001: 'bg-red-500/10 text-red-700 border-red-300',
      system: 'bg-gray-500/10 text-gray-700 border-gray-300',
      konfiguration: 'bg-yellow-500/10 text-yellow-700 border-yellow-300'
    };
    return colors[category] || 'bg-gray-500/10 text-gray-700 border-gray-300';
  };

  const groupedByDate = entries.reduce((acc, entry) => {
    const date = format(new Date(entry.timestamp), 'yyyy-MM-dd');
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(entry);
    return acc;
  }, {} as Record<string, ChangeLogEntry[]>);

  const stats = {
    total: entries.length,
    today: entries.filter(e => 
      format(new Date(e.timestamp), 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
    ).length,
    week: entries.filter(e => 
      new Date(e.timestamp) >= subDays(new Date(), 7)
    ).length,
    byCategory: Object.entries(
      entries.reduce((acc, entry) => {
        acc[entry.category] = (acc[entry.category] || 0) + 1;
        return acc;
      }, {} as Record<string, number>)
    ).map(([category, count]) => ({ category, count }))
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <AdminNavigation />
      <main className="pt-24 pb-24">
        <div className="container mx-auto px-4 max-w-7xl">
          <Breadcrumbs items={[
            { label: "Admin" },
            { label: "Changelog" }
          ]} />

          <div className="mb-8 flex flex-col items-start justify-between gap-4 mt-6 sm:flex-row">
            <div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-2">
                System Changelog
              </h1>
              <p className="text-xl text-muted-foreground">
                Protokoll aller Änderungen im Admin-Bereich
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button 
                variant="outline" 
                onClick={() => handleExport('md')}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Markdown
              </Button>
              <Button 
                variant="outline" 
                onClick={() => handleExport('csv')}
                className="flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                CSV
              </Button>
            </div>
          </div>

          {/* Statistiken */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Gesamt
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.total}</div>
                <p className="text-xs text-muted-foreground mt-1">Einträge</p>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Heute
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.today}</div>
                <p className="text-xs text-muted-foreground mt-1">Änderungen</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Letzte 7 Tage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats.week}</div>
                <p className="text-xs text-muted-foreground mt-1">Änderungen</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  Aktionen
                </CardTitle>
              </CardHeader>
              <CardContent>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" size="sm" className="w-full">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Löschen
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Changelog löschen?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Diese Aktion kann nicht rückgängig gemacht werden. Alle Changelog-Einträge werden dauerhaft gelöscht.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                      <AlertDialogAction onClick={handleClear}>
                        Löschen
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardContent>
            </Card>
          </div>

          {/* Filter */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filter & Suche
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Suchen..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9"
                  />
                </div>

                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger>
                    <SelectValue placeholder="Kategorie wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Kategorien</SelectItem>
                    <SelectItem value="datenschutz">Datenschutz</SelectItem>
                    <SelectItem value="dokumente">Dokumente</SelectItem>
                    <SelectItem value="betroffenenrechte">Betroffenenrechte</SelectItem>
                    <SelectItem value="backup">Backup</SelectItem>
                    <SelectItem value="iso27001">ISO 27001</SelectItem>
                    <SelectItem value="system">System</SelectItem>
                    <SelectItem value="konfiguration">Konfiguration</SelectItem>
                  </SelectContent>
                </Select>

                <Select value={dateFilter} onValueChange={setDateFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="Zeitraum wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Gesamter Zeitraum</SelectItem>
                    <SelectItem value="today">Heute</SelectItem>
                    <SelectItem value="week">Letzte 7 Tage</SelectItem>
                    <SelectItem value="month">Letzter Monat</SelectItem>
                    <SelectItem value="quarter">Letztes Quartal</SelectItem>
                    <SelectItem value="year">Letztes Jahr</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {filteredEntries.length !== entries.length && (
                <Alert className="mt-4">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    {filteredEntries.length} von {entries.length} Einträgen werden angezeigt
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>

          {/* Changelog Einträge */}
          <Tabs defaultValue="timeline" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="timeline">
                <History className="w-4 h-4 mr-2" />
                Timeline
              </TabsTrigger>
              <TabsTrigger value="category">
                <Filter className="w-4 h-4 mr-2" />
                Nach Kategorie
              </TabsTrigger>
            </TabsList>

            <TabsContent value="timeline">
              {filteredEntries.length === 0 ? (
                <Alert>
                  <FileText className="h-4 w-4" />
                  <AlertDescription>
                    Keine Einträge gefunden. Änderungen werden automatisch protokolliert.
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-6">
                  {Object.entries(groupedByDate).map(([date, dayEntries]) => (
                    <div key={date}>
                      <div className="flex items-center gap-2 mb-4">
                        <Calendar className="w-4 h-4 text-muted-foreground" />
                        <h3 className="text-lg font-semibold">
                          {format(new Date(date), "EEEE, dd. MMMM yyyy", { locale: de })}
                        </h3>
                      </div>
                      <div className="space-y-3 pl-6 border-l-2 border-border">
                        {dayEntries.map((entry) => (
                          <Card key={entry.id} className="ml-4">
                            <CardHeader className="pb-3">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2 mb-2">
                                    <Clock className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-sm text-muted-foreground">
                                      {format(new Date(entry.timestamp), "HH:mm:ss", { locale: de })}
                                    </span>
                                    <Badge variant="outline" className={getCategoryColor(entry.category)}>
                                      {getCategoryLabel(entry.category)}
                                    </Badge>
                                  </div>
                                  <CardTitle className="text-lg">{entry.action}</CardTitle>
                                </div>
                              </div>
                            </CardHeader>
                            <CardContent>
                              <p className="text-muted-foreground mb-2">{entry.description}</p>
                              {entry.details && (
                                <details className="mt-3">
                                  <summary className="cursor-pointer text-sm text-primary hover:underline">
                                    Details anzeigen
                                  </summary>
                                  <pre className="mt-2 p-3 bg-muted rounded text-xs overflow-x-auto">
                                    {JSON.stringify(entry.details, null, 2)}
                                  </pre>
                                </details>
                              )}
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="category">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {stats.byCategory.map(({ category, count }) => (
                  <Card key={category}>
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between">
                        <span>{getCategoryLabel(category as ChangeLogEntry['category'])}</span>
                        <Badge variant="outline">{count} Einträge</Badge>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {filterByCategory(category as ChangeLogEntry['category']).slice(0, 5).map((entry) => (
                          <div key={entry.id} className="p-3 border rounded-lg">
                            <div className="flex items-start justify-between mb-1">
                              <p className="font-medium text-sm">{entry.action}</p>
                              <span className="text-xs text-muted-foreground">
                                {format(new Date(entry.timestamp), "dd.MM. HH:mm", { locale: de })}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground">{entry.description}</p>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default AdminChangelog;
